/**
 * Auth Sync Handler Component
 * Syncs the signed-in Clerk user with the backend once per session.
 * Also captures ?ref= invite codes for attribution at sync.
 */
import { useState, useEffect } from "react";
import { useAuth, useUser } from "@clerk/clerk-react";
import { authAPI } from "../../services/api";
import { captureReferralFromSearch } from "../../services/api/app/referral";
import logger from "../../utils/logger";

function AuthSyncHandler() {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const { user } = useUser();
  const [hasSynced, setHasSynced] = useState(false);

  useEffect(() => {
    captureReferralFromSearch();
  }, []);

  useEffect(() => {
    // Only sync once per session
    if (isLoaded && isSignedIn && user && !hasSynced) {
      setHasSynced(true);

      // Run sync in background without blocking UI
      (async () => {
        try {
          logger.debug("Requesting authentication token from Clerk");

          // Add timeout to prevent hanging
          const tokenPromise = getToken();
          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error("Token request timeout")), 5000)
          );

          const token = await Promise.race([tokenPromise, timeoutPromise]);

          if (token) {
            logger.debug("Token obtained successfully", { tokenLength: token.length });

            // Sync with backend (with timeout)
            logger.info("Syncing user data with backend");
            const syncPromise = authAPI.syncUser(token);
            const syncTimeout = new Promise((_, reject) =>
              setTimeout(() => reject(new Error("Sync request timeout")), 10000)
            );

            await Promise.race([syncPromise, syncTimeout]);
            logger.info("User data synced with backend successfully");
          }
        } catch (error) {
          // Silently fail - don't block the UI
          // Only log if it's not a connection/timeout error (expected in some cases)
          if (!error.message.includes("timeout") && !error.message.includes("Cannot connect")) {
            logger.error("User sync failed", error);
          } else {
            logger.warn("User sync failed - backend may be offline", error);
          }
        }
      })();
    }
  }, [isLoaded, isSignedIn, user, hasSynced, getToken]);

  return null; // This component doesn't render anything
}

export default AuthSyncHandler;
